"use client"
import { useState, useRef, useEffect } from "react"
import gsap from "gsap"
import Image from "next/image"

export default function ImagePlaceholder({ imageUrl = "/parallax/p1.jpeg", alt = "", isVertical = false }) {
  const [loaded, setLoaded] = useState(false)
  const dotRef = useRef(null)
  const imgRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    // Position the dot at center on mount
    const dot = dotRef.current
    const img = imgRef.current
    const container = containerRef.current

    if (!dot || !img || !container) return

    gsap.set(dot, {
      xPercent: -50,
      yPercent: -150,
      scale: .25,
      opacity: 1
    })

  }, [])

  const handleImageLoad = () => {
    const dot = dotRef.current
    const img = imgRef.current
    const container = containerRef.current
    if (!dot || !img || !container) return

    const tl = gsap.timeline({
      onComplete: () => setLoaded(true),
      once: true,
      scrollTrigger: {
        trigger: container,
        start: isVertical ? "top bottom" : "top left", // adjust as needed
        end: isVertical ? "top center" : "bottom left-=20%",
      }
    })

    // Start animation from center
    tl.to(dot, {
      duration: 0.25, // Small initial animation
      ease: "expo.inOut",
      yPercent: -50
    })
      .to(dot, {
        duration: 0.75,
        scale: 50, // Larger scale for better coverage
        ease: "expo.in"
      })
      .fromTo(
        img,
        { opacity: 0 },
        { opacity: 1, duration: 0.45, ease: "expo.inOut", zIndex: 99 },
        "-=0.5" // Start revealing the image while dot is still fading
      )
      .to(dot, {
        opacity: 0
      }) 
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
  }

  return (
    <figure ref={containerRef} className="relative min-h-[200px] max-h-[40vh] lg:h-[70vh] lg:max-h-[70vh] aspect-video overflow-hidden min-w-1/2 rounded-3xl max-w-full w-full lg:max-w-[60vw]">
      <div ref={dotRef} className="absolute left-1/2 top-1/2 w-10 h-10 bg-[#FF3C5C] rounded-full z-10 p-6" />

      <Image ref={imgRef} src={imageUrl} fill alt={alt} onLoad={handleImageLoad} className={`w-full h-full max-w-full object-cover transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`} />
    </figure>
  )
}
